import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { OrganizationUsageLimits } from 'types/organization_usage_limits';
import { RequireAtLeastOne } from 'types/utility_types';

import { updateQueriesData } from 'services/update_queries_data';
import { useAPI } from 'hooks/api/useAPI';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';

type UseUpdateUsageLimits = RequireAtLeastOne<{
  cloud_storage_bytes_limit?: number | null;
  file_processing_minutes_limit?: number | null;
  monthly_active_live_stream_limit?: number | null;
  running_stream_limit?: number | null;
  recording_retention_days?: number | null;
}>;

export function useUpdateUsageLimits({
  onMutate,
  onSettled,
  onError,
  ...options
}: UseMutationOptions<
  OrganizationUsageLimits,
  APIError,
  UseUpdateUsageLimits
> = {}) {
  const queryClient = useQueryClient();

  const { client } = useAPI();
  const { data: organization } = useCurrentOrganization();

  return useMutation(
    async (params) => {
      if (!organization) {
        return Promise.reject(new Error('Missing organization id.'));
      }

      const { data } = await client.put<OrganizationUsageLimits>(
        `/internal/orgs/${organization.id}/usage_limits`,
        { ...params }
      );

      return data;
    },
    {
      ...options,
      async onMutate(params) {
        await queryClient.cancelQueries(['usage-limits']);

        onMutate?.(params);

        if (organization) {
          return updateQueriesData<OrganizationUsageLimits>({
            queryClient,
            singleQueryKey: ['usage-limits'],
            ids: [organization.id],
            updateData: (usageLimits) => ({ ...usageLimits, ...params }),
          });
        }
      },
      async onError(error, params, context) {
        onError?.(error, params, context);

        if (organization) {
          context?.previousLists?.forEach(([queryKey, data]) =>
            queryClient.setQueryData(queryKey, data)
          );
          context?.previousSingles?.forEach(([queryKey, data]) =>
            queryClient.setQueryData(queryKey, data)
          );
        }
      },
      onSettled(...args) {
        onSettled?.(...args);
        queryClient.invalidateQueries(['usage-limits']);
      },
    }
  );
}
